import React, { useState, useEffect, useRef } from 'react';
import { Box, Fade, Typography } from '@mui/material';

interface SplashScreenProps {
  onComplete?: () => void;
  duration?: number; // Duration in milliseconds (optional, will auto-detect GIF loop if not provided)
}

const SplashScreen: React.FC<SplashScreenProps> = ({ 
  onComplete, 
  duration 
}) => {
  const [show, setShow] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [gifDuration, setGifDuration] = useState<number | null>(duration || null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const animationStartTime = useRef<number | null>(null);
  const firstFrameData = useRef<string | null>(null);
  const loopDetected = useRef(false);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    const handleImageLoad = async () => {
      setIsImageLoaded(true);
      // If duration is provided, use it directly
      if (duration) {
        setGifDuration(duration);
        return;
      }

      // Otherwise, detect when the GIF completes one loop
      await detectGifLoop();
    };

    const detectGifLoop = async () => {
      const img = imgRef.current;
      if (!img) return;

      // Wait for GIF to start playing
      await new Promise(resolve => setTimeout(resolve, 800));

      // Create a hidden canvas to capture frames
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) {
        // Fallback to default duration if canvas is not available
        setGifDuration(6000);
        return;
      }

      // Wait a bit more to ensure we're not capturing the very first frame
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Capture the first frame's pixel data to compare against
      // Sample only a portion of pixels for faster comparison
      ctx.drawImage(img, 0, 0);
      const firstFrameImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      animationStartTime.current = Date.now();
      
      // Store a sample of the first frame (every 10th pixel for speed)
      const firstFrameSample: number[] = [];
      for (let i = 0; i < firstFrameImageData.data.length; i += 40) { // Every 10th pixel (4 bytes per pixel)
        firstFrameSample.push(
          firstFrameImageData.data[i],
          firstFrameImageData.data[i + 1],
          firstFrameImageData.data[i + 2]
        );
      }
      
      let frameCount = 0;
      let hasFrameChanged = false;
      let lastSample: number[] = [];

      // Check periodically for when the GIF loops back to the first frame
      const checkInterval = setInterval(() => {
        if (loopDetected.current) {
          clearInterval(checkInterval);
          return;
        }

        frameCount++;
        
        // Draw current frame
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
        const currentFrameImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Sample current frame
        const currentFrameSample: number[] = [];
        for (let i = 0; i < currentFrameImageData.data.length; i += 40) {
          currentFrameSample.push(
            currentFrameImageData.data[i],
            currentFrameImageData.data[i + 1],
            currentFrameImageData.data[i + 2]
          );
        }

        // Track if frames have changed (animation is playing)
        const sampleChanged = JSON.stringify(currentFrameSample) !== JSON.stringify(lastSample);
        if (sampleChanged) {
          hasFrameChanged = true;
          lastSample = currentFrameSample;
        }

        // Compare samples - frames match if samples are very similar
        let matchCount = 0;
        for (let i = 0; i < Math.min(firstFrameSample.length, currentFrameSample.length); i += 3) {
          const r1 = firstFrameSample[i];
          const g1 = firstFrameSample[i + 1];
          const b1 = firstFrameSample[i + 2];
          const r2 = currentFrameSample[i];
          const g2 = currentFrameSample[i + 1];
          const b2 = currentFrameSample[i + 2];
          
          // Allow small differences
          if (Math.abs(r1 - r2) <= 10 && Math.abs(g1 - g2) <= 10 && Math.abs(b1 - b2) <= 10) {
            matchCount++;
          }
        }
        
        // Consider it a match if 90% of sampled pixels are similar
        const framesMatch = matchCount > (firstFrameSample.length / 3) * 0.9;
        const elapsed = Date.now() - (animationStartTime.current || 0);
        
        // If frames match and enough time has passed, we've looped
        if (framesMatch && elapsed > 2000 && frameCount > 10 && hasFrameChanged) {
          loopDetected.current = true;
          setGifDuration(elapsed);
          clearInterval(checkInterval);
          return;
        }
      }, 200); // Check every 200ms

      // Fallback: if loop not detected within 30 seconds, use a longer default
      // This ensures we wait long enough for longer GIFs
      setTimeout(() => {
        if (!loopDetected.current) {
          clearInterval(checkInterval);
          // Use a longer default to ensure GIF has time to play
          setGifDuration(12000); // Default to 12 seconds
        }
      }, 30000);
    };

    img.addEventListener('load', handleImageLoad);
    
    // If image is already loaded
    if (img.complete) {
      handleImageLoad();
    }

    return () => {
      img.removeEventListener('load', handleImageLoad);
    };
  }, [duration]);

  useEffect(() => {
    if (gifDuration === null) return;

    // Wait for one complete GIF animation cycle
    // Add a small buffer to ensure the GIF has fully completed
    // Also enforce a minimum duration of 3 seconds
    const minDuration = 5050;
    const totalDuration = Math.max(gifDuration + 500, minDuration);
    
    // Start fade out animation slightly before hiding
    const fadeOutTimer = setTimeout(() => {
      setFadeOut(true);
    }, totalDuration - 300); // Start fade 300ms before hiding

    // Hide splash screen after GIF completes one cycle
    const hideTimer = setTimeout(() => {
      setShow(false);
      if (onComplete) {
        onComplete();
      }
    }, totalDuration);

    return () => {
      clearTimeout(fadeOutTimer);
      clearTimeout(hideTimer);
    };
  }, [gifDuration, onComplete]);

  if (!show) return null;

  return (
    <Fade in={!fadeOut} timeout={300}>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#FFFFFF',
          zIndex: 9999,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            ref={imgRef}
            component="img"
            src="/vertical.gif"
            alt="ifinds Logo"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: isImageLoaded ? 1 : 0,
              transition: 'opacity 200ms ease-in',
            }}
          />
          {/* <Typography style={{color:'white',fontSize:26}}>iFinds</Typography> */}
        </Box>
      </Box>
    </Fade>
  );
};

export default SplashScreen;

